import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../lib/api";
import { getLang, locale, t, useLang } from "../lib/i18n";
import { useMe } from "../hooks/useMe";
import { learnerPose, useLearners, type Learner } from "../hooks/useLearners";
import { Alert } from "../components/Form";
import { Art } from "../ui/Art";
import { Banner, BigButton, BigLink, SectionTitle, StatTile, pastel } from "../ui/kit";
import { Icon } from "../ui/Icon";

const yearLabel = (y: number | null) => (y === null ? t("Belum dipilih") : y === 0 ? t("Tadika") : `${t("Tahun")} ${y}`);
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString(locale(), { day: "numeric", month: "short" }) : t("Belum mula"));

function SignInPrompt() {
  return (
    <div className="rounded-3xl bg-white p-6 text-center shadow-sm ring-1 ring-black/5">
      <Art id="mascot/duo-hello" alt="" className="mx-auto h-32 w-32" />
      <p className="mt-2 text-lg font-extrabold text-slate-900">{t("Log masuk untuk menambah anak dan melihat perkembangan mereka.")}</p>
      <div className="mt-4"><BigLink to="/login">{t("Log masuk")}</BigLink></div>
    </div>
  );
}

/** /anak — the children of this account: add, edit, choose who is learning, and open each child's progress. */
export function AnakListPage() {
  useLang();
  const { me, loading } = useMe();
  const { learners, max, current, select, reload } = useLearners(Boolean(me));
  const [editing, setEditing] = useState<Learner | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function remove(l: Learner) {
    if (!window.confirm(t("Padam {name}? Sejarah belajarnya disimpan tetapi tidak lagi dipaparkan.", { name: l.name }))) return;
    try { await api(`/api/learners/${l.id}`, { method: "DELETE" }); if (current?.id === l.id) select(null); await reload(); } catch (e) { setError(errorMessage(e)); }
  }

  return (
    <>
      <Banner art="cover/akaun" kicker={t("Akaun ibu bapa")} title={t("Anak-anak saya")} tagline={t("Tambah nama anak, pilih maskot, dan lihat perkembangan setiap seorang.")} />
      {error && <Alert kind="error">{error}</Alert>}
      {loading ? null : !me ? <SignInPrompt /> : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {(learners ?? []).map((l, i) => (
              <article key={l.id} className={`${pastel(i)} rounded-3xl p-4 shadow-sm ring-1 ring-black/5`}>
                <div className="flex items-center gap-3">
                  <Art id={`mascot/${learnerPose(l.gender)}`} alt="" className="h-20 w-20 shrink-0 rounded-2xl bg-white/70" />
                  <div className="min-w-0">
                    <p className="truncate text-xl font-black text-slate-900">{l.name}</p>
                    <p className="text-sm font-bold text-slate-600">{yearLabel(l.year)} · {l.gender === "girl" ? t("Perempuan") : t("Lelaki")}</p>
                    <p className="mt-1 text-xs font-bold text-slate-600">
                      {t("{n} nota", { n: l.lessons ?? 0 })} · {t("{n} kuiz", { n: l.quizzes ?? 0 })}{l.avgPercent != null ? ` · ${l.avgPercent}%` : ""}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link to={`/anak/${l.id}`} className="inline-flex min-h-11 items-center rounded-full bg-brand px-4 text-sm font-extrabold text-white">{t("Lihat perkembangan")}</Link>
                  <button type="button" onClick={() => select(l.id)} aria-pressed={current?.id === l.id} className={`min-h-11 rounded-full px-4 text-sm font-extrabold ${current?.id === l.id ? "bg-emerald-500 text-white" : "bg-white text-slate-800 ring-1 ring-black/10"}`}>
                    {current?.id === l.id ? t("Sedang belajar") : t("Pilih")}
                  </button>
                  <button type="button" onClick={() => setEditing(l)} className="min-h-11 rounded-full bg-white px-4 text-sm font-extrabold text-slate-800 ring-1 ring-black/10">{t("Ubah")}</button>
                  <button type="button" onClick={() => remove(l)} className="min-h-11 rounded-full bg-white px-4 text-sm font-extrabold text-rose-700 ring-1 ring-black/10">{t("Padam")}</button>
                </div>
              </article>
            ))}
          </div>
          {learners && learners.length < max && !editing && (
            <div className="mt-4"><BigButton onClick={() => setEditing("new")}>{t("+ Tambah anak")}</BigButton></div>
          )}
          {learners && learners.length >= max && <p className="mt-3 text-sm font-bold text-slate-600">{t("Sudah mencapai had {n} anak.", { n: max })}</p>}
          {editing && <LearnerForm initial={editing === "new" ? null : editing} onDone={async () => { setEditing(null); await reload(); }} onCancel={() => setEditing(null)} />}
        </>
      )}
    </>
  );
}

function LearnerForm({ initial, onDone, onCancel }: { initial: Learner | null; onDone: () => void | Promise<void>; onCancel: () => void }) {
  const [name, setName] = useState(initial?.name ?? "");
  const [gender, setGender] = useState<"boy" | "girl">(initial?.gender ?? "boy");
  const [year, setYear] = useState<number | null>(initial?.year ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    if (!name.trim()) { setError(t("Sila masukkan nama anak.")); return; }
    setBusy(true); setError(null);
    try {
      const json = { name: name.trim(), gender, year };
      if (initial) await api(`/api/learners/${initial.id}`, { method: "PATCH", json }); else await api("/api/learners", { method: "POST", json });
      await onDone();
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }
  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="mt-4 rounded-3xl bg-white p-4 shadow-md ring-1 ring-black/5">
      <h2 className="text-lg font-black text-slate-900">{initial ? t("Ubah maklumat anak") : t("Tambah anak")}</h2>
      {error && <Alert kind="error">{error}</Alert>}
      <label className="mt-3 block text-sm font-extrabold text-slate-700" htmlFor="learner-name">{t("Nama anak")}</label>
      <input id="learner-name" value={name} maxLength={30} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-2xl border border-slate-300 px-4 py-3 text-lg font-bold" autoComplete="off" />
      <p className="mt-4 text-sm font-extrabold text-slate-700">{t("Pilih maskot")}</p>
      <div className="mt-2 grid grid-cols-2 gap-3">
        {(["boy", "girl"] as const).map((g) => (
          <button key={g} type="button" onClick={() => setGender(g)} aria-pressed={gender === g}
            className={`rounded-3xl p-3 text-center font-extrabold transition active:scale-95 ${gender === g ? "bg-sky-100 ring-4 ring-sky-400" : "bg-slate-50 ring-1 ring-slate-200"}`}>
            <Art id={`mascot/${learnerPose(g)}`} alt="" className="mx-auto h-24 w-24" />
            {g === "girl" ? t("Perempuan") : t("Lelaki")}
          </button>
        ))}
      </div>
      <label className="mt-4 block text-sm font-extrabold text-slate-700" htmlFor="learner-year">{t("Tahap persekolahan")}</label>
      <select id="learner-year" value={year === null ? "" : String(year)} onChange={(e) => setYear(e.target.value === "" ? null : Number(e.target.value))} className="mt-1 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-lg font-bold">
        <option value="">{t("Belum dipilih")}</option>
        <option value="0">{t("Tadika")}</option>
        {[1, 2, 3, 4, 5, 6].map((y) => <option key={y} value={y}>{t("Tahun")} {y}</option>)}
      </select>
      <div className="mt-4 flex flex-wrap gap-2">
        <BigButton onClick={() => void save()} disabled={busy}>{t("Simpan")}</BigButton>
        <BigButton tone="soft" onClick={onCancel}>{t("Batal")}</BigButton>
      </div>
    </form>
  );
}

type Stats = {
  learner: Learner;
  totals: { lessons: number; quizzes: number; avgPercent: number | null; games: number; tadikaRounds: number };
  days: { day: string; n: number }[];
  subjects: { subject: string; nameMs: string; nameEn: string; units: number; mastery: number }[];
  weak: { topicId: string; title: string; titleEn: string | null; year: number; subjectMs: string; subjectEn: string; mastery: number; tp: number | null }[];
  recent: { topicId: string; title: string; titleEn: string | null; subjectMs: string; subjectEn: string; score: number; max: number; at: string }[];
};
const barTone = (p: number) => (p >= 80 ? "bg-emerald-500" : p >= 60 ? "bg-amber-400" : "bg-rose-400");

/** /anak/:id — the parent's progress page for one child. */
export function AnakStatsPage() {
  useLang();
  const { id = "" } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState<Stats | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { api<Stats>(`/api/learners/${id}/stats`).then(setS).catch((e) => { setError(errorMessage(e)); }); }, [id]);
  const en = getLang() === "en";
  const sub = (ms: string, e: string) => (en ? e || ms : ms);
  const top = s ? Math.max(1, ...s.days.map((d) => d.n)) : 1;
  const weakest = s?.subjects[0];
  return (
    <>
      <button type="button" onClick={() => nav("/anak")} className="mb-3 inline-flex min-h-11 items-center gap-2 rounded-full bg-white px-4 font-extrabold text-slate-800 shadow-sm ring-1 ring-black/5"><Icon name="arrow" className="h-4 w-4 rotate-180" />{t("Semua anak")}</button>
      {error && <Alert kind="error">{error}</Alert>}
      {!s && !error && <p className="text-slate-600">{t("Memuatkan…")}</p>}
      {s && (
        <>
          <section className="flex items-center gap-4 rounded-3xl bg-gradient-to-r from-sky-200 via-pink-100 to-amber-100 p-4 shadow-sm">
            <Art id={`mascot/${learnerPose(s.learner.gender)}`} alt="" className="h-24 w-24 shrink-0" />
            <div>
              <h1 className="text-2xl font-black text-slate-900">{s.learner.name}</h1>
              <p className="font-bold text-slate-700">{yearLabel(s.learner.year)}</p>
            </div>
          </section>

          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile tone="pastel-blue" icon={<Icon name="book" className="h-6 w-6" />} value={s.totals.lessons} label={t("Nota dibaca")} />
            <StatTile tone="pastel-green" icon={<Icon name="pencil" className="h-6 w-6" />} value={s.totals.quizzes} label={t("Kuiz diambil")} />
            <StatTile tone="pastel-yellow" icon={<Icon name="star" className="h-6 w-6" />} value={s.totals.avgPercent != null ? `${s.totals.avgPercent}%` : "–"} label={t("Purata markah kuiz")} />
            <StatTile tone="pastel-pink" icon={<Icon name="game" className="h-6 w-6" />} value={s.totals.games} label={t("Permainan dimain")} />
          </div>

          <SectionTitle>{t("14 hari terakhir")}</SectionTitle>
          <div className="rounded-3xl bg-white p-4 shadow-sm ring-1 ring-black/5">
            <div className="flex h-32 items-end gap-1" role="img" aria-label={t("Aktiviti belajar 14 hari terakhir")}>
              {s.days.map((d) => (
                <div key={d.day} className="flex h-full flex-1 flex-col justify-end" title={`${d.day}: ${d.n}`}>
                  <div className={`w-full rounded-t-lg ${d.n ? "bg-sky-400" : "bg-slate-200"}`} style={{ height: `${d.n ? Math.max(8, (d.n / top) * 100) : 4}%` }} />
                </div>
              ))}
            </div>
            <div className="mt-1 flex justify-between text-[11px] font-bold text-slate-500"><span>{s.days[0]?.day.slice(5)}</span><span>{s.days[s.days.length - 1]?.day.slice(5)}</span></div>
          </div>

          <SectionTitle>{t("Penguasaan mengikut subjek")}</SectionTitle>
          {s.subjects.length === 0 ? <p className="rounded-3xl bg-white p-4 font-bold text-slate-600 ring-1 ring-black/5">{t("Belum ada kuiz. Minta {name} cuba kuiz pertama.", { name: s.learner.name })}</p> : (
            <ul className="space-y-2">
              {s.subjects.map((x) => (
                <li key={x.subject} className="rounded-3xl bg-white p-3 shadow-sm ring-1 ring-black/5">
                  <div className="flex items-center justify-between gap-2"><span className="font-extrabold text-slate-900">{sub(x.nameMs, x.nameEn)}</span><span className="text-sm font-black text-slate-700">{x.mastery}%</span></div>
                  <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-100"><div className={`h-full rounded-full ${barTone(x.mastery)}`} style={{ width: `${x.mastery}%` }} /></div>
                </li>
              ))}
            </ul>
          )}

          {s.weak.length > 0 && (
            <>
              <SectionTitle>{t("Perlu diperbaiki")}</SectionTitle>
              {weakest && weakest.mastery < 70 && <p className="mb-2 rounded-2xl bg-amber-50 p-3 text-sm font-bold text-amber-900">{t("Cadangan: bantu {name} mengulang kaji {subject} dahulu.", { name: s.learner.name, subject: sub(weakest.nameMs, weakest.nameEn) })}</p>}
              <ul className="space-y-2">
                {s.weak.map((w) => (
                  <li key={w.topicId}>
                    <Link to={`/belajar/unit/${w.topicId}`} className="flex items-center justify-between gap-3 rounded-3xl bg-rose-50 p-3 ring-1 ring-rose-100">
                      <span className="min-w-0"><span className="block truncate font-extrabold text-slate-900">{en ? w.titleEn || w.title : w.title}</span><span className="text-xs font-bold text-slate-600">{sub(w.subjectMs, w.subjectEn)} · {t("Tahun")} {w.year}</span></span>
                      <span className="shrink-0 rounded-full bg-white px-3 py-1 text-sm font-black text-rose-700">{w.mastery}%</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}

          {s.recent.length > 0 && (
            <>
              <SectionTitle>{t("Kuiz terkini")}</SectionTitle>
              <ul className="space-y-2">
                {s.recent.map((r, i) => {
                  const pct = Math.round((100 * r.score) / r.max);
                  return (
                    <li key={`${r.topicId}-${i}`} className="flex items-center justify-between gap-3 rounded-3xl bg-white p-3 shadow-sm ring-1 ring-black/5">
                      <span className="min-w-0"><span className="block truncate font-extrabold text-slate-900">{en ? r.titleEn || r.title : r.title}</span><span className="text-xs font-bold text-slate-500">{sub(r.subjectMs, r.subjectEn)} · {when(r.at)}</span></span>
                      <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-black text-white ${barTone(pct)}`}>{r.score}/{r.max}</span>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </>
      )}
    </>
  );
}
